import { supabase } from "@/lib/supabase";
import { useAuthStore } from "@/stores/useAuthStore";
import {
  checkOrgFile,
  ORG_FILES_BUCKET,
  orgFilePath,
  type OrgFile,
  type OrgMessage,
  type OrgPoll,
  type OrgQuestRow,
  type OrgTaskRow,
  type PollAudience,
} from "@/lib/orgHub";
import type { ContentI18n } from "@/lib/questContent/types";
import type { QuestStatus, TaskStatus } from "@/lib/types";
import type { CreateOrgPollInput, IOrgHubService } from "@/services/interfaces/IOrgHubService";

/** How much chat history the hub loads. Older messages stay in the table. */
const CHAT_PAGE = 200;

/** Keep the stable `org_*` code as the message; orgErrorKey maps it. */
function fail(error: { message: string; details?: string | null }): never {
  // details carries extra context some codes need (org_rate_limited: retry_at).
  throw Object.assign(new Error(error.message), { details: error.details ?? null });
}

async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) fail(error);
  return data as T;
}

interface RollupRow {
  id: string;
  title: string;
  status: string;
  deadline: string | null;
  total_budget_usd: number | string;
  currency: string;
  creator_id: string;
  creator_name: string | null;
  created_at: string;
  updated_at: string;
  source_lang: string | null;
  i18n: ContentI18n | null;
  tasks: Array<{
    id: string;
    title: string;
    status: string;
    deadline: string | null;
    budget_usd: number | string | null;
    assignee_id: string | null;
    assignee_name: string | null;
    source_lang: string | null;
    i18n: ContentI18n | null;
  }> | null;
  /** 20261013150000; absent until that migration is applied. */
  is_clone?: boolean | null;
}

export function mapRollupRow(r: RollupRow): OrgQuestRow {
  return {
    id: r.id,
    title: r.title,
    status: r.status as QuestStatus,
    deadline: r.deadline,
    totalBudgetUsd: Number(r.total_budget_usd),
    currency: r.currency,
    creatorId: r.creator_id,
    creatorName: r.creator_name,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    sourceLang: r.source_lang,
    i18n: r.i18n,
    isClone: r.is_clone ?? false,
    tasks: (r.tasks ?? []).map((t): OrgTaskRow => ({
      id: t.id,
      title: t.title,
      status: t.status as TaskStatus,
      deadline: t.deadline,
      budgetUsd: t.budget_usd === null ? null : Number(t.budget_usd),
      assigneeId: t.assignee_id,
      assigneeName: t.assignee_name,
      sourceLang: t.source_lang,
      i18n: t.i18n,
    })),
  };
}

export class SupabaseOrgHubService implements IOrgHubService {
  // ─── Work ──────────────────────────────────────────────────────────────
  async listQuests(orgId: string, chainId: number): Promise<OrgQuestRow[]> {
    const rows = await rpc<RollupRow[]>("org_quest_rollup", { p_org: orgId, p_chain_id: chainId });
    return (rows ?? []).map(mapRollupRow);
  }

  // ─── Chat ──────────────────────────────────────────────────────────────
  async listMessages(orgId: string): Promise<OrgMessage[]> {
    const { data, error } = await supabase
      .from("organization_messages")
      .select("id, organization_id, sender_id, body, created_at")
      .eq("organization_id", orgId)
      .order("created_at", { ascending: false })
      .limit(CHAT_PAGE);
    if (error) fail(error);
    return ((data ?? []) as {
      id: string; organization_id: string; sender_id: string; body: string; created_at: string;
    }[])
      .map((m) => ({ id: m.id, organizationId: m.organization_id, senderId: m.sender_id, body: m.body, createdAt: m.created_at }))
      .reverse();
  }

  async sendMessage(orgId: string, body: string): Promise<void> {
    // sender_id defaults to the caller in the DB, and the policy pins it.
    const { error } = await supabase.from("organization_messages").insert({ organization_id: orgId, body });
    if (error) fail(error);
  }

  async hasSentMessage(orgId: string): Promise<boolean> {
    return this.authoredAny("organization_messages", "sender_id", orgId);
  }

  async hasUploadedFile(orgId: string): Promise<boolean> {
    return this.authoredAny("organization_files", "uploaded_by", orgId);
  }

  async hasPostedQuest(orgId: string): Promise<boolean> {
    // quests_select: a creator always reads their own quests, drafts included.
    return this.authoredAny("quests", "creator_id", orgId);
  }

  /** One row by the caller in this org's `table`? Exact, unlike the hub's paged lists. */
  private async authoredAny(
    table: "organization_messages" | "organization_files" | "quests",
    authorColumn: "sender_id" | "uploaded_by" | "creator_id",
    orgId: string,
  ): Promise<boolean> {
    const viewer = useAuthStore.getState().session?.userId;
    if (!viewer) return false;
    const { data, error } = await supabase
      .from(table)
      .select("id")
      .eq("organization_id", orgId)
      .eq(authorColumn, viewer)
      .limit(1);
    if (error) fail(error);
    return (data ?? []).length > 0;
  }

  async deleteMessage(messageId: string): Promise<void> {
    const { data, error } = await supabase.from("organization_messages").delete().eq("id", messageId).select("id");
    if (error) fail(error);
    // RLS hides a row the caller may not delete: 0 rows, no error.
    if (!data?.length) throw new Error("org_not_admin");
  }

  async getChatReadAt(orgId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from("organization_chat_reads").select("last_read_at").eq("organization_id", orgId).maybeSingle();
    if (error) fail(error);
    return (data as { last_read_at: string } | null)?.last_read_at ?? null;
  }

  async markChatRead(orgId: string, through: string): Promise<void> {
    const { error } = await supabase
      .from("organization_chat_reads")
      .upsert({ organization_id: orgId, last_read_at: through }, { onConflict: "organization_id,user_id" });
    if (error) fail(error);
  }

  // ─── Polls ─────────────────────────────────────────────────────────────
  async listPolls(orgId: string): Promise<OrgPoll[]> {
    const rows = await rpc<Array<{
      id: string; question: string; options: string[]; audience: PollAudience; created_by: string;
      creator_name: string | null; closes_at: string | null; closed_at: string | null; created_at: string;
      counts: number[] | null; my_vote: number | null;
    }>>("org_poll_list", { p_org: orgId });
    return (rows ?? []).map((p) => ({
      id: p.id, question: p.question, options: p.options, audience: p.audience, createdBy: p.created_by,
      creatorName: p.creator_name, closesAt: p.closes_at, closedAt: p.closed_at, createdAt: p.created_at,
      counts: p.counts?.map(Number) ?? null, myVote: p.my_vote,
    }));
  }

  async createPoll(orgId: string, input: CreateOrgPollInput): Promise<string> {
    return rpc<string>("create_org_poll", {
      p_org: orgId,
      p_question: input.question,
      p_options: input.options,
      p_audience: input.audience,
      p_closes_at: input.closesAt,
    });
  }

  async vote(pollId: string, option: number): Promise<void> {
    await rpc<null>("cast_org_poll_vote", { p_poll: pollId, p_option: option });
  }

  async closePoll(pollId: string): Promise<void> {
    await rpc<null>("close_org_poll", { p_poll: pollId });
  }

  // ─── Files ─────────────────────────────────────────────────────────────
  async listFiles(orgId: string): Promise<OrgFile[]> {
    const { data, error } = await supabase
      .from("organization_files")
      .select("id, organization_id, uploaded_by, path, name, mime_type, size_bytes, created_at, uploader:public_profiles!organization_files_uploaded_by_fkey(display_name, handle)")
      .eq("organization_id", orgId)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) fail(error);
    return ((data ?? []) as unknown as Array<{
      id: string; organization_id: string; uploaded_by: string; path: string; name: string; mime_type: string;
      size_bytes: number; created_at: string; uploader: { display_name: string | null; handle: string | null } | null;
    }>).map((f) => ({
      id: f.id, organizationId: f.organization_id, uploadedBy: f.uploaded_by,
      uploaderName: f.uploader?.display_name?.trim() || f.uploader?.handle || null,
      path: f.path, name: f.name, mimeType: f.mime_type, sizeBytes: Number(f.size_bytes), createdAt: f.created_at,
    }));
  }

  async uploadFile(orgId: string, file: File): Promise<void> {
    const check = checkOrgFile(file);
    if (!check.ok) throw new Error(check.error === "tooLarge" ? "org_file_too_large" : "org_file_bad_type");

    const path = orgFilePath(orgId, crypto.randomUUID(), file.name);
    const { error: uploadError } = await supabase.storage
      .from(ORG_FILES_BUCKET).upload(path, file, { contentType: file.type });
    if (uploadError) throw new Error("org_file_upload_failed");

    const { error } = await supabase.from("organization_files").insert({
      organization_id: orgId, path, name: file.name.slice(0, 200), mime_type: file.type, size_bytes: file.size,
    });
    if (error) {
      // Don't leave an object nobody can see in the library.
      await supabase.storage.from(ORG_FILES_BUCKET).remove([path]);
      fail(error);
    }
  }

  async deleteFile(file: Pick<OrgFile, "id" | "path">): Promise<void> {
    // Row first: if the object removal then fails, the file is gone from the
    // library and only an unreachable object is left (no dead row).
    const { data, error } = await supabase.from("organization_files").delete().eq("id", file.id).select("id");
    if (error) fail(error);
    if (!data?.length) throw new Error("org_not_admin");
    await supabase.storage.from(ORG_FILES_BUCKET).remove([file.path]);
  }

  async renameFile(fileId: string, name: string): Promise<void> {
    const { data, error } = await supabase.from("organization_files").update({ name }).eq("id", fileId).select("id");
    if (error) fail(error);
    // RLS hides a row the caller may not rename: zero rows, no error.
    if (!data?.length) throw new Error("org_not_admin");
  }

  async fileUrls(paths: readonly string[]): Promise<Record<string, string>> {
    if (paths.length === 0) return {};
    const { data, error } = await supabase.storage.from(ORG_FILES_BUCKET).createSignedUrls([...paths], 60 * 10);
    if (error || !data) throw new Error("org_file_unavailable");
    const out: Record<string, string> = {};
    for (const d of data) if (d.path && d.signedUrl && !d.error) out[d.path] = d.signedUrl;
    return out;
  }

  async fileUrl(path: string, download?: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from(ORG_FILES_BUCKET)
      .createSignedUrl(path, 60 * 5, download ? { download } : undefined);
    if (error || !data) throw new Error("org_file_unavailable");
    return data.signedUrl;
  }
}
